import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  Avatar,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Autocomplete,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Email,
  Phone,
  LocationCity,
  Cake,
  School,
  CalendarToday,
  ChevronRight,
  Security,
  Help,
  Description,
  Share,
  Logout,
  Edit,
  Payment,
  Language,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { AuthContext } from '../../context/AuthContext';
import ThemeToggle from '../../components/ThemeToggle';
import api from '../../utils/api';
import { COURSE_OPTIONS, INDIAN_CITIES } from '../../utils/indianCities';
import BottomSheet from '../../components/shared/BottomSheet';
import SurfaceCard from '../../components/shared/SurfaceCard';
import PageShell from '../../components/shared/PageShell';
import { RADIUS } from '../../theme/tokens';

const MotionBox = motion(Box);

function StudentProfile({ showSnackbar }) {
  const { isAuthenticated, user, logout, updateUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [logoutOpen, setLogoutOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  const parseCoursesString = (coursesStr) => {
    if (!coursesStr) return [];
    return coursesStr.split(',').map(c => c.trim()).filter(c => c);
  };

  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    age: user?.age || '',
    city: user?.city || '',
    interested_courses: parseCoursesString(user?.interested_courses),
  });

  const handleLogout = () => {
    setLoggingOut(true);
    setLogoutOpen(false);
    navigate('/');
    setTimeout(() => { logout(); }, 0);
  };

  const handleComingSoon = (feature) => {
    showSnackbar(`${feature} - Coming Soon!`, 'info');
  };

  const handleEditProfile = () => {
    setFormData({
      name: user?.name || '',
      phone: user?.phone || '',
      age: user?.age || '',
      city: user?.city || '',
      interested_courses: parseCoursesString(user?.interested_courses),
    });
    setEditError('');
    setEditDialog(true);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setEditError('');
  };

  const handleSaveProfile = async () => {
    try {
      setEditLoading(true);
      setEditError('');

      if (!formData.name || !formData.name.trim()) {
        setEditError('Name is required');
        setEditLoading(false);
        return;
      }

      const coursesString = Array.isArray(formData.interested_courses)
        ? formData.interested_courses.join(', ')
        : formData.interested_courses;

      const updateData = {
        name: formData.name.trim(),
        phone: formData.phone?.trim() || null,
        age: formData.age ? parseInt(formData.age) : null,
        city: formData.city?.trim() || null,
        interested_courses: coursesString || null,
      };

      const response = await api.put(`/users/${user.id}`, updateData);

      if (response.data.success || response.data.data || response.data) {
        const backendUser = response.data.data?.user || response.data.data || response.data.user;
        const mergedUser = {
          ...user,
          ...updateData,
          ...(backendUser || {}),
        };

        console.log('📝 Updating user:', { original: user, merged: mergedUser });

        updateUser(mergedUser);
        setEditDialog(false);

        const coinsAwarded = response.data.data?.coinsAwarded || 0;
        if (coinsAwarded > 0) {
          showSnackbar(`Profile updated! You earned ${coinsAwarded} BML Coins for completing your profile!`, 'success');
        } else {
          showSnackbar(response.data.message || 'Profile updated successfully!', 'success');
        }
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      if (err.response?.status === 401) {
        setEditError('Session expired. Please login again.');
        setTimeout(() => { logout(); navigate('/'); }, 2000);
      } else if (err.response?.status === 403) {
        setEditError('You do not have permission to update this profile.');
      } else {
        setEditError(err.response?.data?.message || 'Failed to update profile. Please try again.');
      }
    } finally {
      setEditLoading(false);
    }
  };

  const profileFields = [
    { icon: <Email />, label: 'Email', value: user?.email, show: true },
    { icon: <Phone />, label: 'Phone', value: user?.phone, show: user?.phone },
    { icon: <Cake />, label: 'Age', value: user?.age ? `${user.age} years` : null, show: user?.age },
    { icon: <LocationCity />, label: 'City', value: user?.city, show: user?.city },
    { icon: <School />, label: 'Courses', value: user?.interested_courses, show: user?.interested_courses },
    {
      icon: <CalendarToday />,
      label: 'Member Since',
      value: user?.created_at ? new Date(user.created_at).toLocaleDateString('en-IN', {
        year: 'numeric', month: 'short', day: 'numeric',
      }) : null,
      show: user?.created_at,
    },
  ];

  const settingsSections = [
    {
      title: 'Account',
      items: [
        { icon: <Edit />, label: 'Edit Profile', action: handleEditProfile },
        { icon: <Security />, label: 'Privacy & Security', action: () => handleComingSoon('Privacy & Security') },
        { icon: <Payment />, label: 'Payment Methods', action: () => handleComingSoon('Payment Methods') },
      ],
    },
    {
      title: 'Support',
      items: [
        { icon: <Help />, label: 'Help Center', action: () => handleComingSoon('Help Center') },
        { icon: <Description />, label: 'Terms & Conditions', action: () => handleComingSoon('Terms & Conditions') },
        { icon: <Share />, label: 'Share App', action: () => handleComingSoon('Share') },
      ],
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 },
  };

  // Logout confirmation content — shared between BottomSheet and Dialog
  const LogoutContent = () => (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Are you sure you want to logout?
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          onClick={() => setLogoutOpen(false)}
          variant="text"
          sx={{ flex: 1, textTransform: 'none', borderRadius: RADIUS.md }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleLogout}
          variant="contained"
          color="error"
          sx={{ flex: 2, textTransform: 'none', borderRadius: RADIUS.md }}
        >
          Logout
        </Button>
      </Box>
    </>
  );

  if (!isAuthenticated && !loggingOut) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} sx={{ textAlign: 'center', px: 3 }}>
          <Avatar sx={{ width: 100, height: 100, margin: '0 auto', mb: 2, bgcolor: 'background.subtle' }} />
          <Typography variant="h5" fontWeight={700} gutterBottom>Not signed in</Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Sign in to view your profile
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/login')}
            sx={{ borderRadius: RADIUS.lg, px: 4 }}
          >
            Sign In
          </Button>
        </MotionBox>
      </Box>
    );
  }

  if (loggingOut) return null;

  return (
    <PageShell
      title="Profile"
      maxWidth={isMobile ? 'sm' : 'md'}
      action={
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <ThemeToggle />
          <IconButton onClick={handleEditProfile} size="small" aria-label="Edit profile">
            <Edit />
          </IconButton>
        </Box>
      }
    >
      <MotionBox initial="hidden" animate="visible" variants={containerVariants}>

          {/* Profile summary card */}
          <SurfaceCard radius="lg" elevation="sm" sx={{ mb: 2 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main', fontWeight: 700, fontSize: '1.5rem' }}>
                {user?.name?.charAt(0)}
              </Avatar>
              <Box flex={1} minWidth={0}>
                <Typography variant="h3" fontWeight={700} noWrap>{user?.name}</Typography>
                <Typography variant="overline" color="text.secondary">{user?.role}</Typography>
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
              Member ID: {user?.id?.slice(0, 8)}
            </Typography>
          </SurfaceCard>

          {/* Profile Information */}
          <SurfaceCard radius="lg" elevation="sm" sx={{ mb: 2, p: 0 }}>
            <Box p={2} pb={1}>
              <Typography variant="subtitle1" fontWeight={700}>Profile information</Typography>
            </Box>
            <List disablePadding>
              {profileFields.filter(f => f.show).map((field, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <Divider />}
                  <ListItem sx={{ py: 1.5, px: 2 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {React.cloneElement(field.icon, { color: 'primary', fontSize: 'small' })}
                    </ListItemIcon>
                    <ListItemText
                      primary={field.label}
                      secondary={field.value || 'Not provided'}
                      primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                      secondaryTypographyProps={{ variant: 'body2', fontWeight: 500, color: 'text.primary' }}
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </SurfaceCard>

          {/* Settings sections */}
          {settingsSections.map((section, si) => (
            <SurfaceCard key={si} radius="lg" elevation="sm" sx={{ mb: 2, p: 0 }}>
              <Box p={2} pb={1}>
                <Typography variant="subtitle1" fontWeight={700}>{section.title}</Typography>
              </Box>
              <List disablePadding>
                {section.items.map((item, ii) => (
                  <React.Fragment key={ii}>
                    {ii > 0 && <Divider />}
                    <ListItemButton onClick={item.action} sx={{ py: 1.5, px: 2 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {React.cloneElement(item.icon, { color: 'action', fontSize: 'small' })}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        secondary={item.value}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      <ChevronRight color="action" fontSize="small" />
                    </ListItemButton>
                  </React.Fragment>
                ))}
              </List>
            </SurfaceCard>
          ))}

          {/* Preferences: Theme */}
          <SurfaceCard radius="lg" elevation="sm" sx={{ mb: 2, p: 0 }}>
            <Box p={2} pb={1}>
              <Typography variant="subtitle1" fontWeight={700}>Preferences</Typography>
            </Box>
            <List disablePadding>
              <ListItem sx={{ py: 1.5, px: 2 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <ThemeToggle sx={{ p: 0 }} />
                </ListItemIcon>
                <ListItemText
                  primary="Theme"
                  secondary="Toggle light / dark mode"
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
              <Divider />
              <ListItemButton onClick={() => handleComingSoon('Language Settings')} sx={{ py: 1.5, px: 2 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Language fontSize="small" color="action" />
                </ListItemIcon>
                <ListItemText
                  primary="Language"
                  secondary="English"
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
                <ChevronRight color="action" fontSize="small" />
              </ListItemButton>
            </List>
          </SurfaceCard>

          {/* Logout */}
          <SurfaceCard radius="lg" elevation="sm" sx={{ p: 0 }}>
            <ListItemButton onClick={() => setLogoutOpen(true)} sx={{ py: 2, px: 2 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Logout color="error" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Logout"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600, color: 'error.main' }}
              />
            </ListItemButton>
          </SurfaceCard>

          {/* App info */}
          <Box textAlign="center" mt={3} mb={2}>
            <Typography variant="caption" color="text.secondary">BookMyLibrary v1.0.0</Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              &copy; {new Date().getFullYear()} All rights reserved
            </Typography>
          </Box>
        </MotionBox>

        {/* Edit Profile Dialog */}
        <Dialog
          open={editDialog}
          onClose={() => !editLoading && setEditDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: RADIUS.xl } }}
        >
          <DialogTitle>
            <Typography variant="h6" fontWeight={700}>Edit profile</Typography>
            <Typography variant="caption" color="text.secondary">Update your personal information</Typography>
          </DialogTitle>
          <DialogContent>
            {editError && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: RADIUS.md }}>{editError}</Alert>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
              <Box>
                <TextField
                  label="Full Name"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  required
                  fullWidth
                  disabled={editLoading}
                />
                {user?.name && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Current: {user.name}
                  </Typography>
                )}
              </Box>

              <Box>
                <TextField
                  label="Phone Number"
                  value={formData.phone}
                  onChange={(e) => handleFormChange('phone', e.target.value)}
                  placeholder="+91 1234567890"
                  fullWidth
                  disabled={editLoading}
                />
                {user?.phone ? (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Current: {user.phone}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>Not set</Typography>
                )}
              </Box>

              <Box>
                <TextField
                  label="Age"
                  type="number"
                  value={formData.age}
                  onChange={(e) => handleFormChange('age', e.target.value)}
                  inputProps={{ min: 1, max: 150 }}
                  fullWidth
                  disabled={editLoading}
                />
                {user?.age ? (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Current: {user.age} years
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>Not set</Typography>
                )}
              </Box>

              <Box>
                <Autocomplete
                  freeSolo
                  options={INDIAN_CITIES}
                  value={formData.city || ''}
                  onChange={(event, newValue) => handleFormChange('city', newValue || '')}
                  onInputChange={(event, newInputValue) => {
                    if (event && event.type === 'change') handleFormChange('city', newInputValue);
                  }}
                  disabled={editLoading}
                  renderInput={(params) => (
                    <TextField {...params} label="City" placeholder="Select or type city..." />
                  )}
                />
                {user?.city ? (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Current: {user.city}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>Not set</Typography>
                )}
              </Box>

              <Box>
                <Autocomplete
                  multiple
                  options={COURSE_OPTIONS}
                  value={formData.interested_courses}
                  onChange={(event, newValue) => handleFormChange('interested_courses', newValue)}
                  disabled={editLoading}
                  renderInput={(params) => (
                    <TextField {...params} label="Interested Courses" placeholder="Select courses..." />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option}
                        size="small"
                        {...getTagProps({ index })}
                        sx={{ bgcolor: 'primary.soft', color: 'primary.dark' }}
                      />
                    ))
                  }
                />
                {user?.interested_courses ? (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Current: {user.interested_courses}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>Not set</Typography>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button
              onClick={() => setEditDialog(false)}
              disabled={editLoading}
              sx={{ textTransform: 'none', borderRadius: RADIUS.md }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveProfile}
              variant="contained"
              disabled={editLoading}
              startIcon={editLoading && <CircularProgress size={20} />}
              sx={{ textTransform: 'none', borderRadius: RADIUS.md }}
            >
              {editLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Logout — BottomSheet on mobile, Dialog on desktop */}
        {isMobile ? (
          <BottomSheet
            open={logoutOpen}
            onClose={() => setLogoutOpen(false)}
            title="Confirm logout"
          >
            <LogoutContent />
          </BottomSheet>
        ) : (
          <Dialog open={logoutOpen} onClose={() => setLogoutOpen(false)} PaperProps={{ sx: { borderRadius: RADIUS.xl } }}>
            <DialogTitle>
              <Typography variant="h6" fontWeight={700}>Confirm logout</Typography>
            </DialogTitle>
            <DialogContent>
              <LogoutContent />
            </DialogContent>
            <DialogActions sx={{ p: 2, pt: 0 }} />
          </Dialog>
        )}
    </PageShell>
  );
}

export default StudentProfile;
